import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { UserVerificationRequirementsV1_Org_Coursera_Userverification_UserVerificationRequirementState as UserVerificationState } from '__generated__/graphql-types';

import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import LearnerProfileContext from 'bundles/page-config-common/contexts/consumer/LearnerProfileContext';
import type { SkillRole } from 'bundles/page-config-common/contexts/consumer/LearnerProfileContext';
import GetLearnerProfileQuery from 'bundles/page-config-common/queries/GetLearnerProfileQuery.graphql';
import GetUserVerificationRequirementsQuery from 'bundles/page-config-common/queries/GetUserVerificationRequirements.graphql';
import type {
  GetLearnerProfileHomeQuery,
  GetLearnerProfileHomeQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/GetLearnerProfileQuery';
import type {
  GetUserVerificationRequirementsQuery as GetUserVerificationRequirementsQueryType,
  GetUserVerificationRequirementsQueryVariables,
} from 'bundles/page-config-common/queries/__generated__/GetUserVerificationRequirements';
import { showPathwaysUserBased } from 'bundles/unified-common/utils/showPathways';

type Props = {
  children: React.ReactNode;
  ignorePathwaysRequirement?: boolean;
};

function skipLoad(ignorePathwaysRequirement: boolean) {
  if (!isAuthenticatedUser()) {
    return true;
  }

  if (ignorePathwaysRequirement) {
    return false;
  } else {
    return !showPathwaysUserBased();
  }
}

/**
 * @description This provider is used to fetch the learner profile data
 */
const LearnerProfileProvider = (props: Props) => {
  const { ignorePathwaysRequirement = false, children } = props;

  const skip = skipLoad(ignorePathwaysRequirement);

  const { data, loading } = useQuery<GetLearnerProfileHomeQuery, GetLearnerProfileHomeQueryVariables>(
    GetLearnerProfileQuery,
    {
      context: {
        clientName: 'gatewayGql',
      },
      skip,
      errorPolicy: 'all',
      ssr: true,
    }
  );

  const { data: userVerificationData, loading: userVerificationDataLoading } = useQuery<
    GetUserVerificationRequirementsQueryType,
    GetUserVerificationRequirementsQueryVariables
  >(GetUserVerificationRequirementsQuery, {
    context: {
      clientName: 'gatewayGql',
    },
    variables: {
      userId: [userGet().id?.toString()],
    },
    skip,
    errorPolicy: 'all',
    ssr: true,
  });

  const learningGoal = data?.LearnerProfile?.me?.learningPrograms?.learningGoal || undefined;
  const writeInRoles = useMemo(
    () =>
      data?.LearnerProfile?.me?.occupationPreferences
        ?.map((occupationPreference) => {
          if (occupationPreference.occupation.__typename === 'LearnerProfile_UserGeneratedOccupation') {
            return occupationPreference.occupation.name;
          }

          return undefined;
        })
        .filter(Boolean) || [],
    [data?.LearnerProfile?.me?.occupationPreferences]
  );

  // Simple roles are considered roles that just have a name but no real pathway recs
  const simpleRoles: Array<string> = [];
  const skillRoles: Array<SkillRole> = [];

  data?.LearnerProfile?.me?.careerRoles?.forEach((role) => {
    const numOfPathways = role?.pathways?.entities?.elements.length ?? 0;
    if (numOfPathways > 0) {
      skillRoles.push(role);
    } else if (role?.name) {
      simpleRoles.push(role.name);
    }
  });

  const userVerificationRequirements =
    userVerificationData?.NaptimeQueries.UserVerificationRequirementsV1Resource.multiGet?.elements?.[0];
  const isVerifiedUser =
    userVerificationRequirements?.userVerificationRequirementState === UserVerificationState.RequirementMet;

  return (
    <LearnerProfileContext.Provider
      value={{
        roles: [...skillRoles, ...simpleRoles, ...writeInRoles],
        skillRoles,
        learningGoal,
        isVerifiedUser,
        userVerificationDataLoading,
        isLearnerProfileLoading: loading,
      }}
    >
      {children}
    </LearnerProfileContext.Provider>
  );
};

export default LearnerProfileProvider;
